/**
 * useRoomPresenceManager Hook
 *
 * Consolidated hook that manages all Ably room presence concerns:
 * - Channel connection and attachment
 * - Entering/leaving presence
 * - Listening to presence changes
 *
 * This replaces the need to use useAblyRoom + useRoomPresence + usePresenceListener separately.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import type * as Ably from 'ably';
import type {
  PlayerPresence,
  GameStartingEvent,
  GameStartEvent,
  MatchmakingTimerStartedEvent,
  MatchmakingTimerResetEvent,
  MatchStartFailedEvent
} from 'shared-types';
import { useSkinStore } from '@/shared/stores/useSkinStore';
import { getAblyClient } from '@/lib/ably-client';
import { toError } from '@/lib/error-utils';
import { getLobbyChannelName } from '@/lib/ably-utils';
import { generateRandomUsername } from '@/lib/username-generator';

interface UseRoomPresenceManagerOptions {
  roomCode: string | null;
  userId: string;
  username?: string;
  skip?: boolean;
  onPresenceChange?: (members: PlayerPresence[]) => void;
  onGameStarting?: (event: GameStartingEvent) => void;
  onGameStart?: (event: GameStartEvent) => void;
  onMatchmakingTimerStarted?: (event: MatchmakingTimerStartedEvent) => void;
  onMatchmakingTimerReset?: (event: MatchmakingTimerResetEvent) => void;
  onMatchStartFailed?: (event: MatchStartFailedEvent) => void;
}

interface UseRoomPresenceManagerReturn {
  // Channel state
  channel: Ably.RealtimeChannel | null;
  isAttached: boolean;
  // Presence state
  isPresent: boolean;
  presenceData: PlayerPresence | null;
  presenceMembers: PlayerPresence[];
  memberCount: number;
  // Actions
  updatePresence: (data: Partial<PlayerPresence>) => Promise<void>;
  refreshPresence: () => Promise<void>;
  // Errors
  error: Error | null;
}

/**
 * Unified hook to manage all room presence concerns
 *
 * @param options - Configuration options
 * @param options.roomCode - The room code (6-char alphanumeric)
 * @param options.userId - Current user's ID
 * @param options.username - Optional username (defaults to random if not provided)
 * @param options.skip - Skip all operations until true (default: false)
 * @param options.onPresenceChange - Optional callback when presence changes
 * @returns Consolidated presence state and actions
 */
export function useRoomPresenceManager({
  roomCode,
  userId,
  username,
  skip = false,
  onPresenceChange,
  onGameStarting,
  onGameStart,
  onMatchmakingTimerStarted,
  onMatchmakingTimerReset,
  onMatchStartFailed
}: UseRoomPresenceManagerOptions): UseRoomPresenceManagerReturn {
  // Read skin preferences for presence data
  const pawnSkinId = useSkinStore((s) => s.pawnSkinId);
  const diceSkinId = useSkinStore((s) => s.diceSkinId);
  const baseSkinId = useSkinStore((s) => s.baseSkinId);
  const plateauxSkinId = useSkinStore((s) => s.plateauxSkinId);

  // Channel state
  const [channel, setChannel] = useState<Ably.RealtimeChannel | null>(null);
  const [isAttached, setIsAttached] = useState(false);

  // Presence state
  const [isPresent, setIsPresent] = useState(false);
  const [presenceData, setPresenceData] = useState<PlayerPresence | null>(null);
  const [presenceMembersMap, setPresenceMembersMap] = useState<Map<string, PlayerPresence>>(
    new Map()
  );

  // Sync skin preferences to presence when they change while in a room
  useEffect(() => {
    if (!channel || !isPresent || !presenceData) return;
    const updatedData: PlayerPresence = {
      ...presenceData,
      skinPreferences: {
        pawn: pawnSkinId,
        dice: diceSkinId,
        base: baseSkinId,
        plateaux: plateauxSkinId
      }
    };
    setPresenceData(updatedData);
    void channel.presence.update(updatedData);
  }, [pawnSkinId, diceSkinId, baseSkinId, plateauxSkinId]);

  // Error state
  const [error, setError] = useState<Error | null>(null);

  // Use ref to store callback to avoid triggering effects when it changes
  const onPresenceChangeRef = useRef(onPresenceChange);
  const onGameStartingRef = useRef(onGameStarting);
  const onGameStartRef = useRef(onGameStart);
  const onMatchmakingTimerStartedRef = useRef(onMatchmakingTimerStarted);
  const onMatchmakingTimerResetRef = useRef(onMatchmakingTimerReset);
  const onMatchStartFailedRef = useRef(onMatchStartFailed);

  useEffect(() => {
    onPresenceChangeRef.current = onPresenceChange;
  }, [onPresenceChange]);

  useEffect(() => {
    onGameStartingRef.current = onGameStarting;
  }, [onGameStarting]);

  useEffect(() => {
    onGameStartRef.current = onGameStart;
  }, [onGameStart]);

  useEffect(() => {
    onMatchmakingTimerStartedRef.current = onMatchmakingTimerStarted;
  }, [onMatchmakingTimerStarted]);

  useEffect(() => {
    onMatchmakingTimerResetRef.current = onMatchmakingTimerReset;
  }, [onMatchmakingTimerReset]);

  useEffect(() => {
    onMatchStartFailedRef.current = onMatchStartFailed;
  }, [onMatchStartFailed]);

  // Fetch current presence members
  const refreshPresence = useCallback(async (): Promise<void> => {
    if (!channel) {
      return;
    }

    try {
      const members = await channel.presence.get();
      const newMap = new Map<string, PlayerPresence>();
      members.forEach((member) => {
        const playerData = member.data as PlayerPresence;
        newMap.set(playerData.userId, playerData);
      });
      setPresenceMembersMap(newMap);
      setError(null);

      if (onPresenceChangeRef.current) {
        onPresenceChangeRef.current(Array.from(newMap.values()));
      }
    } catch (err) {
      setError(toError(err));
    }
  }, [channel]);

  // Update presence data
  const updatePresence = useCallback(
    async (data: Partial<PlayerPresence>): Promise<void> => {
      if (!channel || !presenceData) {
        throw new Error('Cannot update presence: not in presence set');
      }

      const updatedData: PlayerPresence = {
        ...presenceData,
        ...data
      };

      try {
        await channel.presence.update(updatedData);
        setPresenceData(updatedData);
        setError(null);
      } catch (err) {
        setError(err as Error);
        throw err;
      }
    },
    [channel, presenceData]
  );

  // Main effect: Manage channel, presence, and listeners
  useEffect(() => {
    // Reset state when skipped or no room code
    if (skip || !roomCode || !userId) {
      setChannel(null);
      setIsAttached(false);
      setIsPresent(false);
      setPresenceData(null);
      setPresenceMembersMap(new Map());
      setError(null);
      return;
    }

    const ablyClient = getAblyClient();
    const channelName = getLobbyChannelName(roomCode);
    let mounted = true;

    try {
      // Get or create channel
      const roomChannel = ablyClient.channels.get(channelName);
      setChannel(roomChannel);

      // Handler for presence enter events - O(1) with Map
      const handleEnter = (member: Ably.PresenceMessage) => {
        if (!mounted) return;
        const playerData = member.data as PlayerPresence;
        setPresenceMembersMap((prev) => {
          const next = new Map(prev);
          next.set(playerData.userId, playerData);
          if (onPresenceChangeRef.current) {
            onPresenceChangeRef.current(Array.from(next.values()));
          }
          return next;
        });
      };

      // Handler for presence leave events - O(1) with Map
      const handleLeave = (member: Ably.PresenceMessage) => {
        if (!mounted) return;
        const playerData = member.data as PlayerPresence;
        setPresenceMembersMap((prev) => {
          const next = new Map(prev);
          next.delete(playerData.userId);
          if (onPresenceChangeRef.current) {
            onPresenceChangeRef.current(Array.from(next.values()));
          }
          return next;
        });
      };

      // Handler for presence update events - O(1) with Map
      const handleUpdate = (member: Ably.PresenceMessage) => {
        if (!mounted) return;
        const playerData = member.data as PlayerPresence;
        setPresenceMembersMap((prev) => {
          const next = new Map(prev);
          next.set(playerData.userId, playerData);
          if (onPresenceChangeRef.current) {
            onPresenceChangeRef.current(Array.from(next.values()));
          }
          return next;
        });
      };

      // Handler for game-starting events
      const handleGameStarting = (message: Ably.Message) => {
        if (!mounted) return;
        const event = message.data as GameStartingEvent;
        if (onGameStartingRef.current) {
          onGameStartingRef.current(event);
        }
      };

      // Handler for game-start events
      const handleGameStart = (message: Ably.Message) => {
        if (!mounted) return;
        const event = message.data as GameStartEvent;
        if (onGameStartRef.current) {
          onGameStartRef.current(event);
        }
      };

      // Handler for matchmaking-timer-started events
      const handleMatchmakingTimerStarted = (message: Ably.Message) => {
        if (!mounted) return;
        const event = message.data as MatchmakingTimerStartedEvent;
        if (onMatchmakingTimerStartedRef.current) {
          onMatchmakingTimerStartedRef.current(event);
        }
      };

      // Handler for matchmaking-timer-reset events
      const handleMatchmakingTimerReset = (message: Ably.Message) => {
        if (!mounted) return;
        const event = message.data as MatchmakingTimerResetEvent;
        if (onMatchmakingTimerResetRef.current) {
          onMatchmakingTimerResetRef.current(event);
        }
      };

      // Handler for lobby.match-start-failed events (paid match aborted on SOKA reject)
      const handleMatchStartFailed = (message: Ably.Message) => {
        if (!mounted) return;
        const event = message.data as MatchStartFailedEvent;
        if (onMatchStartFailedRef.current) {
          onMatchStartFailedRef.current(event);
        }
      };

      // Listen for channel state changes
      const stateChangeHandler = (stateChange: Ably.ChannelStateChange) => {
        if (!mounted) return;
        if (stateChange.current === 'attached') {
          setIsAttached(true);
          setError(null);
        } else if (stateChange.current === 'failed') {
          setIsAttached(false);
          const failureReason =
            stateChange.reason instanceof Error ? stateChange.reason : new Error('Channel failed');
          setError(failureReason);
        }
      };

      roomChannel.on(stateChangeHandler);

      // Setup async operations
      const setup = async () => {
        try {
          // 1. Attach to channel
          await roomChannel.attach();
          setIsAttached(true);
          setError(null);

          // 2. Subscribe to presence events FIRST (before entering or fetching)
          // IMPORTANT: Await subscriptions to ensure they're ready before entering presence
          // This prevents race conditions where events are published before subscription is complete
          await Promise.all([
            roomChannel.presence.subscribe('enter', handleEnter),
            roomChannel.presence.subscribe('leave', handleLeave),
            roomChannel.presence.subscribe('update', handleUpdate)
          ]);

          // Subscribe to game events - await to ensure ready before entering presence
          await Promise.all([
            roomChannel.subscribe('game-starting', handleGameStarting),
            roomChannel.subscribe('game-start', handleGameStart),
            roomChannel.subscribe('matchmaking-timer-started', handleMatchmakingTimerStarted),
            roomChannel.subscribe('matchmaking-timer-reset', handleMatchmakingTimerReset),
            roomChannel.subscribe('lobby.match-start-failed', handleMatchStartFailed)
          ]);

          // 3. Fetch initial presence members
          const members = await roomChannel.presence.get();
          const initialMap = new Map<string, PlayerPresence>();
          members.forEach((m) => {
            const playerData = m.data as PlayerPresence;
            initialMap.set(playerData.userId, playerData);
          });
          setPresenceMembersMap(initialMap);

          // 4. Enter presence with real username (or random if not provided)
          const initialPresenceData: PlayerPresence = {
            userId,
            username: username ?? generateRandomUsername(),
            joinedAt: Date.now(),
            status: 'connected',
            skinPreferences: {
              pawn: pawnSkinId,
              dice: diceSkinId,
              base: baseSkinId,
              plateaux: plateauxSkinId
            }
          };
          setPresenceData(initialPresenceData);

          await roomChannel.presence.enter(initialPresenceData);
          setIsPresent(true);

          // 5. After entering, update the map with self and notify callback
          // This ensures the joining player also triggers updatePresence with correct count
          const updatedMap = new Map(initialMap);
          updatedMap.set(userId, initialPresenceData);
          setPresenceMembersMap(updatedMap);

          if (onPresenceChangeRef.current) {
            onPresenceChangeRef.current(Array.from(updatedMap.values()));
          }
        } catch (err) {
          setError(toError(err));
          setIsAttached(false);
          setIsPresent(false);
        }
      };

      void setup();

      // Cleanup
      return () => {
        mounted = false;
        roomChannel.off(stateChangeHandler);

        // Unsubscribe from presence events
        roomChannel.presence.unsubscribe('enter', handleEnter);
        roomChannel.presence.unsubscribe('leave', handleLeave);
        roomChannel.presence.unsubscribe('update', handleUpdate);

        // Unsubscribe from game events (named references prevent zombie listeners)
        roomChannel.unsubscribe('game-starting', handleGameStarting);
        roomChannel.unsubscribe('game-start', handleGameStart);
        roomChannel.unsubscribe('matchmaking-timer-started', handleMatchmakingTimerStarted);
        roomChannel.unsubscribe('matchmaking-timer-reset', handleMatchmakingTimerReset);
        roomChannel.unsubscribe('lobby.match-start-failed', handleMatchStartFailed);

        // Leave presence if still attached
        const channelState = roomChannel.state;
        if (channelState === 'attached' || channelState === 'attaching') {
          roomChannel.presence.leave().catch(() => {
            // Ignore errors during cleanup
          });
        }

        // Detach from channel
        roomChannel.detach().catch(() => {
          // Ignore errors during cleanup
        });
      };
    } catch (err) {
      setError(toError(err));
      return;
    }
  }, [roomCode, userId, skip]);

  // Derive array from Map for consumer convenience
  const presenceMembers = Array.from(presenceMembersMap.values());

  return {
    channel,
    isAttached,
    isPresent,
    presenceData,
    presenceMembers,
    memberCount: presenceMembersMap.size,
    updatePresence,
    refreshPresence,
    error
  };
}
